import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { marked } from 'marked';
import { format } from 'date-fns';
import { Helmet } from 'react-helmet';

// Redux Slices
import { setBoardId, selectBoardId } from '../../redux/Slices/boardIdSlice';
import {
  fetchAttachments,
  uploadAttachment,
  editAttachment,
  deleteAttachment,
  resetStatus,
  setUploadProgress,
  optimisticAddAttachment,
  optimisticUpdateAttachment,
  optimisticDeleteAttachment,
} from '../../redux/Slices/attachmentSlice';
import { mentionedMemberIds, resetMentionedIds } from "../../redux/Slices/mentionedSlice";
import CardMemberSlice, { fetchCardMembers, addCardMember, removeCardMember } from '../../redux/Slices/cardMemberSlice';
import { togglePopover } from '../../redux/Slices/popoverSlice';
import {
  cardData,
  updateCard,
  fetchCardById,
  optimisticUpdateCurrentCard,
  optimisticArchiveCard,
} from '../../redux/Slices/cardSlice';
import { fetchUser } from '../../redux/Slices/thunks';
import { fetchBoardById } from '../../redux/Slices/boardSlice';
import {
  fetchMembers,
  optimisticAddMember,
  optimisticRemoveMember,
} from '../../redux/Slices/memberSlice';
import {
  fetchActivities,
  optimisticAddComment,
  optimisticUpdateComment,
  optimisticDeleteComment,
  addActivityOptimistically,
} from '../../redux/Slices/activitiesSlice';
import {
  fetchChecklists,
  optimisticAddChecklist,
  optimisticUpdateChecklist,
  optimisticDeleteChecklist,
  optimisticAddChecklistItem,
  optimisticUpdateChecklistItem,
  optimisticDeleteChecklistItem,
} from '../../redux/Slices/checklistSlice';
import {
  fetchCardLabels,
  optimisticAddLabelToCard,
  optimisticRemoveLabelFromCard,
  optimisticUpdateLabel,
  optimisticDeleteLabel,
} from '../../redux/Slices/labelSlice';
import { setCardMoveTarget } from '../../redux/Slices/moveCardSlice';
import { fetchBoardLists } from '../../redux/Slices/boardsSlice';
import { showSnackBar } from '../../redux/Slices/snackbarSlice';
import { setTimer, setTimerStatus } from '../../redux/Slices/timerSlice';

// Utils and Config
import { debounce, find, hideTooltips, sanitizeHtml, textTransform, TIMER_STATUS_COLOR, toSlug, uploadFile } from '../../utils/helpers';
import { config } from '../../config';
import { showSuccess, showError, updateUploadProgress } from '../../utils/snackbarHelpers';
import { ActionTypes } from '../../sockets/ActionTypes';
import { useSocketConnection, useSocketAction, useSocketEvent } from '../../hooks/useSocketManager';

// Components
import CardDetailsSidebar from '../Workspaces/CardDetailsSidebar';
import SkeletonCardDetails from '../Skeletons/SkeletonCardDetails';
import NotFound from '../400';
import AccessDenied from '../AccessDenied';
import CardDetailsFullLayout from './CardDetailsFullLayout';
import Comments from './Comments';
import PopOver from '../Global/PopOver';
import Checklist from './Checklists';
import Attachments from './Attachments';
import CardMembers from './CardMembers';
import CardLabels from './CardLabels';
import Editor from '../LexicalEditor/Editor';
import Checkbox from '../Global/Checkbox';
import Activities from './Activities';
import MetaTags from '../Global/MetaTags';
import TimeTracker from '../Popovers/TimeTracker';
import LinkPreviewWrapper from '../Global/LinkPreviewWrapper';

// Icons
import IconCardTitle from '../icons/IconCardTitle';
import IconDescription from '../icons/IconDescription';
import Timer from '../icons/Timer';
import EditNote from '../icons/EditNote';
import Check from '../icons/Check';
import Notifications from '../icons/Notifications';
import HourglassTop from '../icons/HourglassTop';

// Hooks
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import { useLightbox, Lightbox } from '../../hooks/useLightbox';
import { useTimer } from '../../hooks/useTimer';

// Styles
import './scss/editor.style.scss';
import './scss/CardDetailsFullLayout.scss';
import BouncingDots from '../Global/BouncingDots';
import KeyboardArrowDown from '../icons/KeyboardArrowDown';



const CardDetails = ({ board, setBoard, isReadOnly = false }) => {


    const { cardId } = useParams();

    const navigate = useNavigate(); // Use navigate hook
    const [isValid, setIsValid] = useState(true);

    // const [card, setCard] = useState({});
    const { card, status } = useSelector((state) => state.card);

    const [title, setTitle] = useState(card?.title || '');
    const [description, setDescription] = useState(card?.description || '');

    const [isModified, setIsModified] = useState(false);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [isTextareaFocused, setIsTextareaFocused] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [accessError, setAccessError] = useState(null);
    const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved'
    const [lastSaved, setLastSaved] = useState(null);


    const { handlePopoverClick } = useHandlePopoverClick();

    const textAreaRef = useRef(null);
    const fileInputRef = useRef(null);

    const [actionListId, setActionListId] = useState(null);
    const [actionListTitle, setActionListTitle] = useState(null);

    const mentionedIds = useSelector((state) => state.mentioned.mentionedMemberIds);
    const { cardMembers, boardMembers, loading } = useSelector(state => state.member);
    const { labelsByCard } = useSelector((state) => state.labels);
    const labels = labelsByCard[cardId] || [];
    const { checklists } = useSelector((state) => state.checklists);
    const { activities } = useSelector((state) => state.activities);

    const timer = useTimer(cardId);

    const initiators = activities
        .filter(activity => activity.actionType === "ADDED_COMMENT")
        .map(activity => activity.initiator);


    const members = cardMembers;

    const boardId = useSelector(selectBoardId);

    const dispatch = useDispatch();

    const { attachmentStatus, attachments, error } = useSelector((state) => state.attachments);

    const { user } = useSelector((state) => state.user);

    const [isFirstLoad, setIsFirstLoad] = useState(true);

    const { images, currentIndex, isOpen, closeLightbox, nextImage, prevImage } = useLightbox();


    const [isExpanded, setIsExpanded] = useState(false);
    const contentRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(null);
    const [layoutStyle, setLayoutStyle] = useState('full'); // 'current' or 'full'

    useEffect(() => {
        if (contentRef.current) {
            setIsOverflowing(200 < contentRef.current.clientHeight);
        }
        else {
            setIsOverflowing(false);
        }

    }, [description]);


    useEffect(() => {
        if (cardId) {
            // Set isFirstLoad to true when cardId changes (new card opened)
            setIsFirstLoad(true);
            setAccessError(null);
            dispatch(fetchCardById(cardId))
                .unwrap()
                .then(() => {
                    // After first successful load, set isFirstLoad to false
                    setIsFirstLoad(false);
                })
                .catch((error) => {
                    console.error("Error fetching card:", error);
                    // Also set isFirstLoad to false on error
                    setIsFirstLoad(false);

                    // Handle access denied errors
                    if (error?.type === 'ACCESS_DENIED' || error?.status === 403) {
                        // Check if this is a workspace binding error
                        const errorData = error?.data || {};
                        const isWorkspaceBindingError = errorData.workspaceBinding === true;

                        let errorMessage = error?.message || 'You do not have access to this card';

                        // Add hint for workspace binding errors
                        if (isWorkspaceBindingError && errorData.hint) {
                            errorMessage = `${errorMessage}\n\n${errorData.hint}`;
                            if (errorData.boundDomain) {
                                errorMessage = `${errorMessage}\n\nBound Domain: ${errorData.boundDomain}`;
                            }
                        }

                        setAccessError({
                            type: 'ACCESS_DENIED',
                            message: errorMessage,
                            isWorkspaceBinding: isWorkspaceBindingError,
                            boundDomain: errorData.boundDomain
                        });
                    } else if (error?.type === 'NOT_FOUND' || error?.status === 404) {
                        setAccessError({
                            type: 'NOT_FOUND',
                            message: error?.message || 'Card not found'
                        });
                    } else {
                        setAccessError({
                            type: 'ERROR',
                            message: error?.message || 'Failed to load card'
                        });
                    }
                });
        }
    }, [cardId, dispatch]);

    useEffect(() => {
        if (card && !isEditingDescription) {
            setTitle(card.title);
            setDescription(card.description);
            setActionListId(card.actionList._id);
            setActionListTitle(card.actionList.title);
            dispatch(cardData(card));
            if (card.board) dispatch(setBoardId(card.board));
        }
    }, [card, isEditingDescription, dispatch]);


    useEffect(() => {
        if (!user) {
            dispatch(fetchUser());
        }
    }, []);

    useEffect(() => {
        dispatch(fetchAttachments(cardId)).then(() => {
            dispatch(resetStatus());
        });

    }, [attachmentStatus, dispatch, cardId]);


    useEffect(() => {
        updateCardDetails(cardId, { attachments });
    }, [attachments]);

    useEffect(() => {
        updateCardDetails(cardId, { cover: card?.cover });
    }, [card?.cover]);

    useEffect(() => {
        updateCardDetails(cardId, { users: cardMembers });
    }, [cardMembers]);

    useEffect(() => {
        updateCardDetails(cardId, { labels });
    }, [labelsByCard]);

    useEffect(() => {
        updateCardDetails(cardId, { checklists });
    }, [checklists]);

    useEffect(() => {
        updateCardDetails(cardId, { comments: initiators });
    }, [activities]);


    useEffect(() => {
        if (boardId && !boardMembers.length > 0) {
            dispatch(fetchMembers({ type: 'board', id: boardId }));
        }

    }, [boardId, dispatch]);

    useEffect(() => {
        if (textAreaRef.current) {
            textAreaRef.current.style.height = '22px';
            textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
        }

    }, [title, isTextareaFocused]);

    // Create a debounced update function
    const debouncedUpdateCard = useRef(
        debounce(async (data) => {
            try {
                setSaveStatus('saving');
                await dispatch(updateCard({ cardId: data.shortId, updatedData: data })).unwrap();
                setSaveStatus('saved');
                setLastSaved(new Date());
                console.log('Auto-saved card description');
            } catch (error) {
                setSaveStatus('idle');
                console.error('Failed to auto-save card:', error);
            }
        }, 1000)
    ).current;

    const handleUpdateCard = async () => {
        const isTitleChanged = title !== card.title;
        const isDescriptionChanged = description !== card.description;
        const oldTitle = card.title;
        const newTitle = title;

        const sanitizedHtmlDes = description;

        // Optimistically update the UI state
        setIsModified(false);
        dispatch(cardData({
            ...card,
            title,
            description: sanitizedHtmlDes,
        }));

        const updatedData = {
            ...card,
            title,
            description: sanitizedHtmlDes,
            mentionedIds
        }

        try {
            await dispatch(updateCard({ cardId: card.shortId, updatedData })).unwrap();

            // Add activity after successful update if details are enabled
            if (card?.isActivityDetails && user?.user) {
                if (isTitleChanged) {
                    dispatch(addActivityOptimistically({
                        cardId,
                        userId: user.user._id,
                        actionType: 'UPDATED_TITLE',
                        details: `updated the title of this card from <b>${oldTitle}</b> to <b>${newTitle}</b>.`,
                        user: user.user
                    }));
                }
                if (isDescriptionChanged) {
                    dispatch(addActivityOptimistically({
                        cardId,
                        userId: user.user._id,
                        actionType: 'UPDATED_DESCRIPTION',
                        details: 'updated the description of this card.',
                        user: user.user
                    }));
                }
            }
        } catch (error) {
            console.error('Failed to update card:', error);
            // Revert on error
            setTitle(oldTitle);
            setDescription(card.description);
        }

        dispatch(resetMentionedIds());
        updateCardDetails(cardId, { title: newTitle, description: sanitizedHtmlDes });
    };

    // Auto-save description
    useEffect(() => {
        if (isEditingDescription && description !== card.description) {
            const sanitizedHtmlDes = description;
            const updatedData = {
                ...card,
                description: sanitizedHtmlDes,
            };
            
            debouncedUpdateCard(updatedData);
            
            // Still update the local board state optimistically
            updateCardDetails(cardId, { description: sanitizedHtmlDes });
        }
    }, [description, isEditingDescription]);

    const handleCloseCardPopup = (event) => {
        if (!card) {
            dispatch(fetchCardById(cardId));
        }


        if (event?.target?.classList?.contains('zoobbe-card-modal-container') || event?.target?.closest('.close-card')) {
            navigate(card.boardLink);
        }
    };

    const [isChecked, setIsChecked] = useState(false);

    // Synchronize isChecked with the card's current status
    useEffect(() => {
        if (card?.dueDate?.status === 'Completed') {
            setIsChecked(true);
        } else {
            setIsChecked(false);
        }

    }, [card]);

    useEffect(() => {
        if (timer) {
            dispatch(setTimer({ cardId, timerData: timer }));
        }
    }, [card]);


    useEffect(() => {

        if (!card || !card?.dueDate?.date) return;

        const currentStatus = card?.dueDate?.status;

        if (card?.dueDate) {
            let newStatus = 'Pending';

            if (isChecked) {
                newStatus = 'Completed';
            } else if (new Date(card.dueDate.date) < new Date()) {
                newStatus = 'Overdue';
            } else if (new Date(card.dueDate.date) >= new Date()) {
                newStatus = 'In-Progress';
            }

            if (currentStatus !== newStatus) {
                updateDueDateStatus(newStatus, card.dueDate);
            }
        }
    }, [isChecked]);

    const updateDueDateStatus = async (status, dueDate) => {
        try {
            

            // Update due date status
            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/duedate`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    
                },
                credentials: 'include',
                body: JSON.stringify({
                    ...dueDate,
                    status,
                }),
            });

            if (response.ok) {
                const updatedCard = await response.json();
                dispatch(cardData(updatedCard));

                // If due date is completed, also mark card as completed
                if (status === 'Completed') {
                    dispatch(updateCard({ cardId, updateData: { completed: true } }));
                }
            } else {
                console.error('Failed to update due date status');
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };


    const [isWatching, setIsWatching] = useState(false);

    useEffect(() => {
        if (card && user) {
            const idExists = card.watchers?.includes(user?.user?._id);
            setIsWatching(idExists);
        }

    }, [card, user]);


    useEffect(() => {
        const fetchBoardData = async () => {
            if (!card?.board || board?.actionLists?.length > 0) return;

            setIsLoading(true);
            setAccessError(null);

            try {
                // Fetch board data
                const boardData = await dispatch(fetchBoardById(card.board)).unwrap();

                // Set initial board with existing cards preserved
                setBoard((prevBoard) => ({
                    ...boardData,
                    actionLists: boardData.actionLists.map((list) => ({
                        ...list,
                        cards: prevBoard?.actionLists?.find((prevList) => prevList.id === list.id)?.cards || [],
                    })),
                }));

            } catch (error) {
                console.error("Error fetching the board:", error);

                // Handle access denied errors
                if (error?.type === 'ACCESS_DENIED' || error?.status === 403) {
                    // Check if this is a workspace binding error
                    const errorData = error?.data || {};
                    const isWorkspaceBindingError = errorData.workspaceBinding === true;

                    let errorMessage = error?.message || 'You do not have access to this card';

                    // Add hint for workspace binding errors
                    if (isWorkspaceBindingError && errorData.hint) {
                        errorMessage = `${errorMessage}\n\n${errorData.hint}`;
                        if (errorData.boundDomain) {
                            errorMessage = `${errorMessage}\n\nBound Domain: ${errorData.boundDomain}`;
                        }
                    }

                    setAccessError({
                        type: 'ACCESS_DENIED',
                        message: errorMessage,
                        isWorkspaceBinding: isWorkspaceBindingError,
                        boundDomain: errorData.boundDomain
                    });
                } else if (error?.type === 'NOT_FOUND' || error?.status === 404) {
                    setAccessError({
                        type: 'NOT_FOUND',
                        message: error?.message || 'Card not found'
                    });
                } else {
                    setAccessError({
                        type: 'ERROR',
                        message: error?.message || 'Failed to load card'
                    });
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchBoardData();
    }, [card?.board]);

    useEffect(() => {
        const handleKeyDown = (event) => {

            if (event.key === 'Escape') {
                navigate(card.boardLink);
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [card?.board]);

    const handleWatch = async () => {
        try {
            
            const endpoint = isWatching ? 'unwatch' : 'watch';
            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/${endpoint}`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    
                },
                credentials: 'include',
                body: JSON.stringify({
                    cardId,
                }),
            });

            if (!response.ok) {
                showError(dispatch, `Failed to ${endpoint} the card`);
                throw new Error(`Failed to ${endpoint} the card`);
            }

            updateCardDetails(cardId, {
                watchers: isWatching
                    ? card.watchers?.filter((watcher) => watcher._id !== user?.user?._id) // Remove user
                    : [...(card.watchers || []), { _id: user?.user?._id }], // Add user
            });


            // Toggle watch state on success
            setIsWatching(!isWatching);

        } catch (error) {
            console.error(error);
        }
    };

    // Assuming `board` is stored in state and you have a `setBoard` function
    const updateCardDetails = (cardId, updatedCardData) => {
        setBoard((prevBoard) => ({
            ...prevBoard,
            actionLists: prevBoard.actionLists.map((actionList) => ({
                ...actionList,
                cards: actionList.cards?.map((card) =>
                    card.shortId === cardId
                        ? {
                            ...card,
                            ...updatedCardData, // Only updates existing card properties
                        }
                        : card
                ),
            })),
        }));
    };

    // Initialize socket connection (app-wide)
    useSocketConnection();

    // Real-time event handlers for card updates
    useSocketAction([
        ActionTypes.CARD_UPDATED,
        ActionTypes.CARD_MOVED,
        ActionTypes.COMMENT_ADDED,
        ActionTypes.COMMENT_DELETED,
        ActionTypes.COMMENT_UPDATED,
        ActionTypes.CHECKLIST_ADDED,
        ActionTypes.CHECKLIST_UPDATED,
        ActionTypes.CHECKLIST_DELETED,
        ActionTypes.CHECKLIST_ITEM_ADDED,
        ActionTypes.CHECKLIST_ITEM_DELETED,
        ActionTypes.CHECKLIST_ITEM_UPDATED,
        ActionTypes.MEMBER_ADDED,
        ActionTypes.MEMBER_REMOVED,
        ActionTypes.ATTACHMENT_ADDED,
        ActionTypes.ATTACHMENT_UPDATED,
        ActionTypes.ATTACHMENT_DELETED,
        ActionTypes.LABEL_ADDED,
        ActionTypes.LABEL_UPDATED,
        ActionTypes.LABEL_DELETED,
        ActionTypes.DUEDATE_ADDED,
        ActionTypes.DUEDATE_UPDATED,
        ActionTypes.DUEDATE_REMOVED,
        ActionTypes.PRIORITY_UPDATED,
        ActionTypes.TIMER_ACTION,
    ], (data) => {

        // Skip events triggered by current user (already handled optimistically)
        if (data.reqUser === user?.user?._id) return;

        console.log('[CardDetails] Real-time event:', data.action);

        switch (data.action) {
            case ActionTypes.CARD_UPDATED:
                // ✅ Update card using optimistic action (no API call)
                if (data.card) {
                    dispatch(optimisticUpdateCurrentCard(data.card));
                }
                break;
            case ActionTypes.CARD_MOVED:
                // ✅ Update card list info optimistically
                if (data.card) {
                    dispatch(optimisticUpdateCurrentCard({
                        actionList: data.targetListId,
                        order: data.targetPosition
                    }));
                }
                dispatch(setCardMoveTarget({
                    targetListId: data.targetListId,
                    targetPosition: data.targetPosition,
                    card: data.card,
                    cardAction: 'MOVE'
                }));
                break;
            case ActionTypes.COMMENT_ADDED:
                // Card data update only; activity refresh handled by CardDetailsFullLayout
                if (data.card) {
                    dispatch(optimisticUpdateCurrentCard(data.card));
                }
                break;
            case ActionTypes.COMMENT_UPDATED:
                // ✅ Update comment using optimistic action (no API call)
                if (data.comment) {
                    dispatch(optimisticUpdateComment({
                        commentId: data.comment._id,
                        updates: data.comment
                    }));
                }
                break;
            case ActionTypes.COMMENT_DELETED:
                // ✅ Delete comment using optimistic action (no API call)
                if (data.commentId) {
                    dispatch(optimisticDeleteComment(data.commentId));
                }
                break;
            case ActionTypes.CHECKLIST_ADDED:
                // ✅ Add checklist using optimistic action (no API call)
                if (data.checklist) {
                    dispatch(optimisticAddChecklist(data.checklist));
                }
                break;
            case ActionTypes.CHECKLIST_UPDATED:
                // ✅ Update checklist using optimistic action (no API call)
                if (data.checklist) {
                    dispatch(optimisticUpdateChecklist({
                        checklistId: data.checklist._id,
                        updates: data.checklist
                    }));
                }
                break;
            case ActionTypes.CHECKLIST_DELETED:
                // ✅ Delete checklist using optimistic action (no API call)
                if (data.checklistId) {
                    dispatch(optimisticDeleteChecklist(data.checklistId));
                }
                break;
            case ActionTypes.CHECKLIST_ITEM_ADDED:
                // ✅ Add checklist item using optimistic action (no API call)
                if (data.checklistId && data.item) {
                    dispatch(optimisticAddChecklistItem({
                        checklistId: data.checklistId,
                        item: data.item
                    }));
                }
                break;
            case ActionTypes.CHECKLIST_ITEM_UPDATED:
                // ✅ Update checklist item using optimistic action (no API call)
                if (data.checklistId && data.item) {
                    dispatch(optimisticUpdateChecklistItem({
                        checklistId: data.checklistId,
                        itemId: data.item._id,
                        updates: data.item
                    }));
                }
                break;
            case ActionTypes.CHECKLIST_ITEM_DELETED:
                // ✅ Delete checklist item using optimistic action (no API call)
                if (data.checklistId && data.itemId) {
                    dispatch(optimisticDeleteChecklistItem({
                        checklistId: data.checklistId,
                        itemId: data.itemId
                    }));
                }
                break;
            case ActionTypes.MEMBER_ADDED:
                // ✅ Add member using optimistic action (no API call)
                if (data.member) {
                    dispatch(optimisticAddMember({
                        type: 'card',
                        member: data.member
                    }));
                }
                break;
            case ActionTypes.MEMBER_REMOVED:
                // ✅ Remove member using optimistic action (no API call)
                if (data.memberId) {
                    dispatch(optimisticRemoveMember({
                        type: 'card',
                        memberId: data.memberId
                    }));
                }
                break;
            case ActionTypes.ATTACHMENT_ADDED:
                // ✅ Add attachment using optimistic action (no API call)
                if (data.attachment) {
                    dispatch(optimisticAddAttachment(data.attachment));
                    // Also update card cover if attachment is cover
                    if (data.attachment.isCover) {
                        dispatch(optimisticUpdateCurrentCard({ cover: data.attachment }));
                    }
                }
                break;
            case ActionTypes.ATTACHMENT_UPDATED:
                // ✅ Update attachment using optimistic action (no API call)
                if (data.attachment) {
                    dispatch(optimisticUpdateAttachment({
                        attachmentId: data.attachment._id,
                        updates: data.attachment
                    }));
                    // Also update card cover if attachment is cover
                    if (data.attachment.isCover) {
                        dispatch(optimisticUpdateCurrentCard({ cover: data.attachment }));
                    }
                }
                break;
            case ActionTypes.ATTACHMENT_DELETED:
                // ✅ Delete attachment using optimistic action (no API call)
                if (data.attachmentId) {
                    dispatch(optimisticDeleteAttachment(data.attachmentId));
                    // Also update card cover if deleted attachment was cover
                    if (data.wasCover) {
                        dispatch(optimisticUpdateCurrentCard({ cover: null }));
                    }
                }
                break;
            case ActionTypes.LABEL_ADDED:
                // ✅ Add label to card using optimistic action (no API call)
                if (data.label) {
                    dispatch(optimisticAddLabelToCard({
                        cardId,
                        label: data.label
                    }));
                }
                break;
            case ActionTypes.LABEL_UPDATED:
                // ✅ Update label using optimistic action (no API call)
                if (data.label && data.boardId) {
                    dispatch(optimisticUpdateLabel({
                        boardId: data.boardId,
                        label: data.label
                    }));
                }
                break;
            case ActionTypes.LABEL_DELETED:
                // ✅ Delete label using optimistic action (no API call)
                if (data.labelId) {
                    // Remove from card
                    dispatch(optimisticRemoveLabelFromCard({
                        cardId,
                        labelId: data.labelId
                    }));
                    // Also remove from board labels
                    if (data.boardId) {
                        dispatch(optimisticDeleteLabel({
                            boardId: data.boardId,
                            labelId: data.labelId
                        }));
                    }
                }
                break;
            case ActionTypes.DUEDATE_ADDED:
            case ActionTypes.DUEDATE_UPDATED:
            case ActionTypes.DUEDATE_REMOVED:
                // ✅ Update due date optimistically
                if (data.card && data.card.dueDate !== undefined) {
                    dispatch(optimisticUpdateCurrentCard({ dueDate: data.card.dueDate }));
                }
                break;
            case ActionTypes.PRIORITY_UPDATED:
                // ✅ Update priority optimistically
                if (data.card && data.card.priority !== undefined) {
                    dispatch(optimisticUpdateCurrentCard({ priority: data.card.priority }));
                }
                break;
            case ActionTypes.TIMER_ACTION:
                // Refresh activities to show timer log
                dispatch(fetchActivities({ cardId, isActivityDetails: card?.isActivityDetails }));
                break;
            case ActionTypes.CHECKLIST_ADDED:
            case ActionTypes.CHECKLIST_UPDATED:
            case ActionTypes.CHECKLIST_DELETED:
            case ActionTypes.CHECKLIST_ITEM_ADDED:
            case ActionTypes.CHECKLIST_ITEM_DELETED:
            case ActionTypes.CHECKLIST_ITEM_UPDATED:
                // Call fetchActivities for checklist actions too
                dispatch(fetchActivities({ cardId, isActivityDetails: card?.isActivityDetails }));
                break;
            default:

                console.log('[CardDetails] Unhandled real-time action:', data.action);
        }
    }, [dispatch, user?.user?._id, cardId]);

    // Upload progress event handler
    useSocketEvent('uploadProgress', (data) => {
        // Keep for backward compatibility with existing upload progress display
        dispatch(setUploadProgress(data));

        // Update snackbar progress if uploadId matches
        if (data.uploadId && window.currentUploadId === data.uploadId) {
            updateUploadProgress(dispatch, data.uploadId, {
                percentage: data.percentage,
                remainingTime: data.remainingTime
            });
        }
    }, [dispatch]);

    useEffect(() => {
        if (isTextareaFocused && textAreaRef.current) {
            textAreaRef.current.focus();
            textAreaRef.current.select(); // Select all text
        }
    }, [isTextareaFocused]);


    const memberIds = members?.map(member => member?._id);
    const isUserMember = user && user.user ? memberIds.includes(user?.user?._id) : false;



    if (isFirstLoad && (status === 'loading' || loading)) {

        return (
            <div className="zoobbe-card-wrapper">
                <div className='zoobbe-card-modal-container'>
                    <div className="preview-loading">
                        <BouncingDots />
                    </div>
                </div>
            </div>
        )
    }

    // Access error handling
    if (accessError) {
        if (accessError.type === 'ACCESS_DENIED') {
            return <AccessDenied message={accessError.message} type="card" />;
        } else if (accessError.type === 'NOT_FOUND') {
            return <NotFound />;
        }
    }

    if (!isValid) {
        return <NotFound />;
    }

    if (!cardId || !card) {
        return null;
    }

    const coverColor = card?.cover?.coverColor;

    const renderCurrentLayout = () => (
        <div className="zoobbe-card-details">
            <div className="close-card-container">
                <div
                    className='layout-switch-btn'
                    onClick={() => {
                        setLayoutStyle('full');
                        hideTooltips();
                    }}
                    data-tooltip-content="Switch Layout"
                    data-tooltip-position="top"
                >
                    <span className="material-symbols-outlined">view_column_2</span>
                </div>
                <div className='close-card'>
                    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M10.586 12 5.293 6.707a1 1 0 0 1 1.414-1.414L12 10.586l5.293-5.293a1 1 0 1 1 1.414 1.414L13.414 12l5.293 5.293a1 1 0 0 1-1.414 1.414L12 13.414l-5.293 5.293a1 1 0 0 1-1.414-1.414z" fill="currentColor" />
                    </svg>
                </div>
            </div>
            {
                card?.cover?.url && (
                    <div className="zoobbe-card-cover-image" style={{ background: coverColor[0] }} >
                        <img className='cover-image' src={card?.cover?.url} alt={card?.cover?.name} loading="lazy" />
                    </div>
                )
            }

            <div className="zoobbe-card-content">
                <div className="zoobbe-card-details-body">
                    <div className={`zoobbe-card-details-header${card?.cover?.url ? ' with-cover' : ''}`}>
                        <div className='card-heading'>
                            <span className="material-symbols-outlined">
                                video_label
                            </span>
                            <div className={`textarea-wrapper ${isTextareaFocused ? "focused" : ""}`}>
                                {isTextareaFocused ? (
                                    <textarea
                                        ref={textAreaRef}
                                        className="zoobbe-card-title-textarea"
                                        value={title}
                                        onChange={(e) => {
                                            setTitle(e.target.value);
                                            updateCardDetails(cardId, { title: e.target.value });
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleUpdateCard();
                                                setIsTextareaFocused(false);
                                            }
                                        }}
                                        onBlur={() => {
                                            handleUpdateCard();
                                            setIsTextareaFocused(false);
                                        }}
                                        spellCheck={isTextareaFocused}
                                    />
                                ) : (
                                    <h1
                                        className="zoobbe-card-title-textarea"
                                        onClick={() => setIsTextareaFocused(true)}
                                        ref={textAreaRef}

                                    >
                                        {title}
                                    </h1>
                                )}
                            </div>
                        </div>
                        <div className="zoobbe-card-details-status">
                            in list
                            <span
                                className='actionlist' id='popover-movecard'
                                onClick={(e) => handlePopoverClick(e, 'moveCard', { cardId, card })}
                                data-tooltip-content="Move"
                                data-tooltip-position="top"
                                data-popover-trigger
                            >{actionListTitle}</span>
                        </div>
                    </div>
                    <div className="zoobbe-card-details-info">
                        <div className='zoobbe-card-details-info-left'>
                            <div className='card-details-top'>
                                <CardMembers cardId={cardId} />

                                <div className="zoobbe-card-members">
                                    <h5>Timer</h5>
                                    <div className='card-timer'>

                                        <div
                                            className='card-timer-icon running'
                                            id='popover-card-timer'
                                            onClick={(e) => handlePopoverClick(e, 'cardTimer', { cardId, cardTimer: timer })}
                                            data-popover-trigger
                                        >
                                            {
                                                timer.mode == 'stopwatch' ? (
                                                    <Timer size={18} color={TIMER_STATUS_COLOR[timer.timerStatus]} />
                                                ) : (
                                                    <HourglassTop size={18} color={TIMER_STATUS_COLOR[timer.timerStatus]} />
                                                )
                                            }





                                        </div>
                                    </div>
                                </div>


                                <div className={`zoobbe-notifications ${isWatching ? 'watching' : ''}`}>
                                    <h5>Notifications</h5>
                                    <div
                                        className='notifications-content'
                                        onClick={handleWatch}
                                    >
                                        <Notifications size={18} />

                                        <span className="zoobbe-watching">
                                            {isWatching ? 'Notifying' : 'Notify'}
                                        </span>
                                        {isWatching && (
                                            <div className="watching-checkmark">
                                                <Check size={16} />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="zoobbe-priority">
                                    <h5>Priority</h5>
                                    <div className='priority-content'>

                                        <div className="priority" id='popover-priority' onClick={(e) => handlePopoverClick(e, 'cardPriority', { cardId, isClosed: false })} data-popover-trigger>

                                            <svg className={`zoobbe-flag-${textTransform(card.priority, 'lowercase')}`} xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#e8eaed"><path d="M0 0h24v24H0V0z" fill="none" /><path d="M14.4 6l-.24-1.2c-.09-.46-.5-.8-.98-.8H6c-.55 0-1 .45-1 1v15c0 .55.45 1 1 1s1-.45 1-1v-6h5.6l.24 1.2c.09.47.5.8.98.8H19c.55 0 1-.45 1-1V7c0-.55-.45-1-1-1h-4.6z" /></svg>

                                            {card.priority}

                                        </div>
                                    </div>
                                </div>


                                {
                                    card?.dueDate?.date && (
                                        <div className="zoobbe-duedate">
                                            <h5>Due Date</h5>
                                            <div className='duedate-content'>
                                                <Checkbox
                                                    checked={isChecked}
                                                    onChange={(checked) => setIsChecked(checked)}
                                                />

                                                <div className="duedate" id='popover-duedate' onClick={(e) => handlePopoverClick(e, 'addDueDate')} data-popover-trigger>
                                                    <span className="zoobbe-watching">
                                                        {format(card.dueDate.date, 'MMM dd')}, {card.dueDate.dueTime}
                                                    </span>
                                                    {
                                                        card.dueDate.status === 'Completed' && (
                                                            <span className="completed-text">Completed</span>
                                                        )
                                                    }
                                                    {
                                                        card.dueDate.status === 'Overdue' && (
                                                            <span className="overdue-text">Overdue</span>
                                                        )
                                                    }

                                                    <KeyboardArrowDown />
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }
                                <CardLabels cardId={cardId} />


                            </div>

                            <div className={`zoobbe-description ${isEditingDescription ? 'currently-editing' : ''}`}>

                                <div className="description-header">
                                    <h3>
                                        <span className="material-symbols-outlined">
                                            subject
                                        </span>
                                        Description
                                    </h3>

                                    {!isEditingDescription ? (
                                        <button className="edit-description-button" onClick={() => setIsEditingDescription(true)}>
                                            <EditNote size={18} />
                                            Edit
                                        </button>
                                    ) : (
                                        <div className="description-edit-actions">
                                            {saveStatus === 'saving' && <span className="save-status-indicator">Saving...</span>}
                                            {saveStatus === 'saved' && lastSaved && (
                                                <span className="save-status-indicator">
                                                    <span>Saved</span> at {format(lastSaved, 'h:mm:ss a')}
                                                </span>
                                            )}
                                            <button className="cancel-editing-content" onClick={() => setIsEditingDescription(false)}>
                                                Close
                                            </button>
                                        </div>
                                    )}

                                </div>

                                {!description && !isEditingDescription && (
                                    <div className="zoobbe-card-no-description" onClick={() => setIsEditingDescription(true)}>Add a more detailed description of the task here... </div>
                                )}

                                {!isEditingDescription && description && (
                                    <div className="card-description-wrapper">
                                        <LinkPreviewWrapper
                                            ref={contentRef}
                                            className={`card-description-content ${isExpanded ? 'expanded' : 'showmore-mask'}`}
                                            dangerouslySetInnerHTML={{ __html: marked(card.description || '') }}
                                            onClick={(e) => {
                                                const anchor = e.target.closest('a');
                                                const image = e.target.closest('img');

                                                if (anchor) {
                                                    window.open(anchor.href, '_blank');
                                                    e.preventDefault();
                                                } else if (image) {
                                                    e.preventDefault();
                                                } else {
                                                    setIsEditingDescription(true);
                                                }
                                            }}
                                        />
                                        {!isExpanded && isOverflowing && <div onClick={() => setIsExpanded(!isExpanded)} className="fade-shadow" />}
                                        {isOverflowing && (
                                            <div className="show-more-button">
                                                <button onClick={() => setIsExpanded(!isExpanded)}>
                                                    {isExpanded ? (
                                                        <span><svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="M480-344 240-584l56-56 184 184 184-184 56 56-240 240Z" /></svg> Show Less</span>
                                                    ) : (
                                                        <span><svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="M480-528 296-344l-56-56 240-240 240 240-56 56-184-184Z" /></svg> Show More</span>
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}



                                {isEditingDescription && card && (
                                    <>
                                        <Editor
                                            value={description}
                                            setDescription={setDescription}
                                            setIsEditingDescription={setIsEditingDescription}
                                            handleUpdateCard={handleUpdateCard}
                                            boardId={boardId}
                                            cardId={cardId}
                                            showActionButtons={false}
                                            context="card_description"
                                        />
                                    </>
                                )}
                            </div>

                            {
                                attachments?.length > 0 && (
                                    <Attachments cardId={cardId} updateCardDetails={updateCardDetails} />
                                )
                            }

                            <Checklist cardId={cardId} />

                            <div className="zoobbe-card-details-footer">
                                <Comments cardId={cardId} isActivityDetails={card.isActivityDetails} />
                                <Activities activities={card.activities} cardId={cardId} />
                            </div>
                        </div>
                        <div className='zoobbe-card-details-info-right'>
                            <CardDetailsSidebar
                                cardId={cardId}
                                card={card}
                                boardId={boardId}
                                setBoard={setBoard}
                                isArchived={card?.archived}
                                isReadOnly={isReadOnly}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="space-bottom"></div>
        </div>
    );

    const renderFullLayout = () => (
        <CardDetailsFullLayout
            card={card}
            cardId={cardId}
            boardId={boardId}
            title={title}
            setTitle={setTitle}
            description={description}
            setDescription={setDescription}
            isEditingDescription={isEditingDescription}
            setIsEditingDescription={setIsEditingDescription}
            isTextareaFocused={isTextareaFocused}
            setIsTextareaFocused={setIsTextareaFocused}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            isOverflowing={isOverflowing}
            contentRef={contentRef}
            textAreaRef={textAreaRef}
            handleUpdateCard={handleUpdateCard}
            updateCardDetails={updateCardDetails}
            actionListTitle={actionListTitle}
            handlePopoverClick={handlePopoverClick}
            isWatching={isWatching}
            handleWatch={handleWatch}
            isChecked={isChecked}
            setIsChecked={setIsChecked}
            attachments={attachments}
            setBoard={setBoard}
            coverColor={coverColor}
            handleCloseCardPopup={handleCloseCardPopup}
            layoutStyle={layoutStyle}
            setLayoutStyle={setLayoutStyle}
            isReadOnly={isReadOnly}
            saveStatus={saveStatus}
            lastSaved={lastSaved}
        />
    );

    return (
        <>
            <MetaTags
                title={`${title} on ${boardId} | Zoobbe`}
                ogUrl={card.parmalink}
            />

            <div className="zoobbe-card-wrapper">
                <div className={`zoobbe-card-modal-container ${layoutStyle === 'full' ? 'full-layout' : ''}`} onClick={handleCloseCardPopup}>
                    {layoutStyle === 'current' ? renderCurrentLayout() : renderFullLayout()}
                </div>

                <Lightbox images={images} attachments={card?.attachments} currentIndex={currentIndex} isOpen={isOpen} closeLightbox={closeLightbox} nextImage={nextImage} prevImage={prevImage} />
            </div>
        </>
    );

};

export default CardDetails;
